import { useEffect, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import NewTierPlansGrid from 'app/components/NewTierPlansGrid';
import { usePlansCatalog } from 'app/hooks/useSubscriptionPlans/usePlansCatalog';
import { useCatalogUnresolvableRedirect } from 'app/hooks/useSubscriptionPlans/useCatalogUnresolvableRedirect';
import useSubscriptionStatus from 'app/hooks/useSubscriptionStatus';
import { openCsWhatsapp } from 'app/utils/csWhatsappSupport';

import {
  trackExplorePlansModalViewed,
  trackPersonalizedPlanCardClicked,
  trackPersonalizedPlanCardViewed,
} from '../trackPlanAndPaymentEvents';
import { useHandleSelectNewTierPlan } from '../useHandleSelectNewTierPlan';
import type { ExplorePlansModalProps } from './types';
import * as S from './styles';

const TITLE = 'Explorar planos';
const DESCRIPTION =
  'Escolha o plano que melhor atende à sua rotina. Você pode trocar de plano a qualquer momento.';
const CATALOG_ERROR_TITLE = 'Não foi possível carregar os planos';
const CATALOG_ERROR_MESSAGE = 'Tente novamente em instantes ou feche e volte mais tarde.';

const PERSONALIZED_CARD = {
  title: 'Plano personalizado',
  subtitle: 'Para quem precisa de mais do que o Black oferece',
  items: [
    'Mais créditos de consultas jurídicas',
    'Usuários e permissões sob medida',
    'Suporte dedicado e onboarding',
    'Soluções personalizadas',
  ],
  ctaLabel: 'Falar com especialista',
  hint: 'Abre o WhatsApp em uma nova aba',
};

export function ExplorePlansModal({
  isOpen,
  onClose,
  formik,
  selectPlan,
  user,
  subscriptionStatus,
  url,
  onCompareAllPlans,
  origin,
  headline,
}: ExplorePlansModalProps) {
  const { plans, isLoading, error, refetch, currentTier } = usePlansCatalog();
  const { planName } = useSubscriptionStatus();
  const title = headline?.title ?? TITLE;
  const description = headline?.description ?? DESCRIPTION;

  useCatalogUnresolvableRedirect({ error, isOpen, onClose });
  const isUnresolvable = error === 'SUBSCRIPTION_STATUS_UNRESOLVABLE';
  const hasCatalogError = Boolean(error);
  const isPersonalizedCardVisible = !isLoading && !hasCatalogError;

  useEffect(() => {
    if (!isOpen || isUnresolvable) {
      return;
    }

    trackExplorePlansModalViewed(origin);
  }, [isOpen, isUnresolvable, origin]);

  const hasTrackedPersonalizedCardViewRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedPersonalizedCardViewRef.current = false;
      return;
    }

    if (!isPersonalizedCardVisible || hasTrackedPersonalizedCardViewRef.current) {
      return;
    }

    hasTrackedPersonalizedCardViewRef.current = true;
    trackPersonalizedPlanCardViewed({ screen: 'explore_plans', currentTier, currentPlan: planName });
  }, [isOpen, isPersonalizedCardVisible, currentTier, planName]);

  const handlePersonalizedCardClick = () => {
    trackPersonalizedPlanCardClicked({ screen: 'explore_plans', currentTier, currentPlan: planName });
    openCsWhatsapp('plans');
  };

  const handleSelectPlan = useHandleSelectNewTierPlan({
    formik,
    selectPlan,
    user,
    subscriptionStatus,
    url,
    offers: plans,
  });

  const gridPlans = plans.map((plan) => ({
    ...plan,
    cta: {
      label: `Assinar ${plan.title}`,
      onClick: () => handleSelectPlan(plan.key, plan.catalogOfferId),
    },
  }));

  const promoNotice = plans.find((plan) => plan.isPromo && plan.promoMessage)?.promoMessage;

  if (isUnresolvable) {
    return null;
  }

  return (
    <S.StyledDialog open={isOpen} onClose={onClose}>
      <S.Head>
        <div>
          <S.Title>{title}</S.Title>
          <S.Description>{description}</S.Description>
        </div>
        <S.CloseButton onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </S.CloseButton>
      </S.Head>

      <S.Body>
        {isLoading ? (
          <S.LoadingState>
            <CircularProgress size={32} />
          </S.LoadingState>
        ) : hasCatalogError ? (
          <S.ResultState>
            <S.ResultIcon $variant="error">
              <ErrorOutlineIcon />
            </S.ResultIcon>
            <S.ResultTitle>{CATALOG_ERROR_TITLE}</S.ResultTitle>
            <S.ResultMessage>{CATALOG_ERROR_MESSAGE}</S.ResultMessage>
          </S.ResultState>
        ) : (
          <NewTierPlansGrid
            plans={gridPlans}
            notice={promoNotice ?? undefined}
            highlightedPlanKey="ultimate_2026"
            density="comfortable"
            personalizedCard={{
              title: PERSONALIZED_CARD.title,
              subtitle: PERSONALIZED_CARD.subtitle,
              items: PERSONALIZED_CARD.items,
              cta: {
                label: PERSONALIZED_CARD.ctaLabel,
                onClick: handlePersonalizedCardClick,
              },
              hint: PERSONALIZED_CARD.hint,
            }}
          />
        )}
      </S.Body>

      <S.Foot $sideBySide>
        {hasCatalogError ? (
          <>
            <S.SecondaryButton onClick={onClose}>Fechar</S.SecondaryButton>
            <S.PrimaryButton type="button" onClick={refetch}>
              Tentar novamente
            </S.PrimaryButton>
          </>
        ) : (
          <>
            <S.SecondaryButton onClick={onCompareAllPlans}>Comparar todos os planos</S.SecondaryButton>
            <S.SecondaryButton onClick={onClose}>Fechar</S.SecondaryButton>
          </>
        )}
      </S.Foot>
    </S.StyledDialog>
  );
}

export default ExplorePlansModal;
